import { useState, useEffect } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import {
  Info, Database, Settings, Share2, HardDrive,
  Clock, Shield, User, Users, Bell, Mail,
} from "lucide-react";
import { PageWrapper, DarkNav, GlassCard, DarkFooter } from "../components/PageShell";

const PRIVACY_CSS = `
  @keyframes privFadeUp {
    from { opacity:0; transform:translateY(20px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes barGrow { from { width:0; } to { width:var(--p,0%); } }
  .priv-section { animation: privFadeUp 0.5s both; }
  .priv-section:nth-child(1)  { animation-delay:0.05s; }
  .priv-section:nth-child(2)  { animation-delay:0.10s; }
  .priv-section:nth-child(3)  { animation-delay:0.15s; }
  .priv-section:nth-child(4)  { animation-delay:0.20s; }
  .priv-section:nth-child(5)  { animation-delay:0.25s; }
  .priv-section:nth-child(6)  { animation-delay:0.30s; }
  .priv-section:nth-child(7)  { animation-delay:0.35s; }
  .priv-section:nth-child(8)  { animation-delay:0.40s; }
  .priv-section:nth-child(9)  { animation-delay:0.45s; }
  .priv-section:nth-child(10) { animation-delay:0.50s; }
  .priv-section:nth-child(11) { animation-delay:0.55s; }
  .priv-toc-btn { transition:all 0.15s; }
  .priv-toc-btn:hover { color:#93C5FD !important; background:rgba(59,130,246,0.1) !important; }
`;

const SECTION_ICONS = {
  intro:     Info,
  collect:   Database,
  use:       Settings,
  share:     Share2,
  cookies:   HardDrive,
  retention: Clock,
  security:  Shield,
  rights:    User,
  children:  Users,
  changes:   Bell,
  contact:   Mail,
};

const SECTIONS = [
  { id:"intro",     label:"Introduction"               },
  { id:"collect",   label:"Data We Collect"            },
  { id:"use",       label:"How We Use Your Data"       },
  { id:"share",     label:"Data Sharing"               },
  { id:"cookies",   label:"Cookies & Storage"          },
  { id:"retention", label:"Data Retention"             },
  { id:"security",  label:"Security"                   },
  { id:"rights",    label:"Your Rights"                },
  { id:"children",  label:"Children's Privacy"         },
  { id:"changes",   label:"Changes to Policy"          },
  { id:"contact",   label:"Contact"                    },
];

function P({ children }) {
  return <p style={{ fontSize:14.5, color:"rgba(255,255,255,0.62)", lineHeight:1.9, margin:"0 0 14px" }}>{children}</p>;
}
function BL({ items }) {
  return (
    <ul style={{ listStyle:"none", padding:0, margin:"10px 0 16px", display:"flex", flexDirection:"column", gap:9 }}>
      {items.map((it,i) => (
        <li key={i} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
          <span style={{ marginTop:9, width:5, height:5, borderRadius:"50%", flexShrink:0, background:"linear-gradient(135deg,#3B82F6,#6366F1)", boxShadow:"0 0 6px rgba(59,130,246,0.5)" }} />
          <span style={{ fontSize:14, color:"rgba(255,255,255,0.62)", lineHeight:1.85 }}>{it}</span>
        </li>
      ))}
    </ul>
  );
}
function InfoBox({ icon, children }) {
  return (
    <div style={{
      background:"rgba(59,130,246,0.08)", border:"1px solid rgba(59,130,246,0.28)",
      borderLeft:"3px solid #3B82F6", borderRadius:"0 12px 12px 0",
      padding:"14px 18px", margin:"14px 0",
      fontSize:13.5, color:"rgba(147,197,253,0.9)", lineHeight:1.75,
      display:"flex", gap:10, alignItems:"flex-start",
    }}>
      {icon && <span style={{ fontSize:16, flexShrink:0, marginTop:1 }}>{icon}</span>}
      <span>{children}</span>
    </div>
  );
}
function SubH({ children }) {
  return <div style={{ fontSize:11, fontWeight:700, color:"#60A5FA", textTransform:"uppercase", letterSpacing:1.1, margin:"18px 0 9px" }}>{children}</div>;
}
function CRow({ label, children }) {
  return (
    <div style={{ display:"flex", gap:12, alignItems:"flex-start", fontSize:14, paddingBottom:10, borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
      <span style={{ width:90, flexShrink:0, color:"rgba(147,197,253,0.6)", fontWeight:500, fontSize:12.5 }}>{label}</span>
      {children}
    </div>
  );
}

function SectionBlock({ id, idx, icon: Icon, children }) {
  const section = SECTIONS.find(s => s.id === id);
  const num = String(idx + 1).padStart(2,"0");
  return (
    <section id={id} className="priv-section" style={{ marginBottom:52, scrollMarginTop:82 }}>
      <div style={{ display:"flex", alignItems:"flex-start", gap:16, marginBottom:20 }}>
        <div style={{
          flexShrink:0, width:42, height:42, borderRadius:12,
          background:"linear-gradient(135deg,rgba(59,130,246,0.18),rgba(99,102,241,0.12))",
          border:"1px solid rgba(59,130,246,0.28)",
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>
          <Icon size={18} color="#60A5FA" />
        </div>
        <div>
          <div style={{ fontSize:10, fontWeight:700, color:"rgba(96,165,250,0.55)", letterSpacing:1.5, marginBottom:3 }}>
            {num}
          </div>
          <h2 style={{ fontSize:18.5, fontWeight:700, color:"#fff", margin:0, letterSpacing:-0.3 }}>
            {section?.label}
          </h2>
        </div>
      </div>
      <div style={{ paddingLeft:58 }}>{children}</div>
      <div style={{ marginTop:36, height:1, background:"linear-gradient(90deg,rgba(59,130,246,0.3),rgba(99,102,241,0.15),transparent)" }} />
    </section>
  );
}

export default function PrivacyPage() {
  const isMobile = useIsMobile();
  const px = isMobile ? 16 : 40;
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState("intro");

  useEffect(() => {
    document.documentElement.classList.add("page-scrollable");
    return () => document.documentElement.classList.remove("page-scrollable");
  }, []);

  useEffect(() => {
    const el = document.querySelector("[data-page-scroll]");
    if (!el) return;
    function onScroll() {
      const pct = el.scrollHeight - el.clientHeight;
      setProgress(pct > 0 ? Math.min(100, Math.round((el.scrollTop / pct) * 100)) : 0);
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const sec = document.getElementById(SECTIONS[i].id);
        if (sec && sec.getBoundingClientRect().top < 160) {
          setActiveId(SECTIONS[i].id); break;
        }
      }
    }
    el.addEventListener("scroll", onScroll, { passive:true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior:"smooth", block:"start" });
  }

  return (
    <PageWrapper>
      <style>{PRIVACY_CSS}</style>

      {/* Reading progress bar */}
      <div style={{
        position:"fixed", top:0, left:0, right:0, height:3, zIndex:200,
        background:"rgba(255,255,255,0.06)",
      }}>
        <div style={{
          height:"100%", width:`${progress}%`,
          background:"linear-gradient(90deg,#3B82F6,#6366F1,#8B5CF6)",
          transition:"width 0.1s linear",
          boxShadow:"0 0 8px rgba(59,130,246,0.6)",
        }} />
      </div>

      <DarkNav px={px} />

      {/* ── HERO ── */}
      <div style={{ padding:`${isMobile?36:56}px ${px}px ${isMobile?24:40}px`, display:"flex", alignItems:"center", gap:20 }}>
        <div style={{
          width:56, height:56, borderRadius:16, flexShrink:0,
          background:"linear-gradient(135deg,rgba(59,130,246,0.2),rgba(99,102,241,0.12))",
          border:"1px solid rgba(59,130,246,0.3)",
          display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:"0 0 24px rgba(59,130,246,0.2)",
        }}>
          <Shield size={26} color="#60A5FA" />
        </div>
        <div>
          <div style={{
            fontSize:10, fontWeight:700, color:"rgba(96,165,250,0.65)",
            letterSpacing:1.5, textTransform:"uppercase", marginBottom:6,
          }}>Legal · Privacy</div>
          <h1 style={{
            fontSize:`clamp(24px,4vw,42px)`, fontWeight:900, margin:"0 0 6px", letterSpacing:-1, lineHeight:1.1,
            background:"linear-gradient(135deg,#fff 0%,#93C5FD 100%)",
            WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
          }}>Privacy Policy</h1>
          <p style={{ fontSize:12.5, color:"rgba(255,255,255,0.3)", margin:0 }}>
            Effective date: June 2026 · Last updated: July 2026
          </p>
        </div>
        {!isMobile && (
          <div style={{ marginLeft:"auto", textAlign:"right" }}>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.25)", marginBottom:6 }}>Reading progress</div>
            <div style={{ fontSize:22, fontWeight:800, color:"#60A5FA" }}>{progress}%</div>
          </div>
        )}
      </div>

      {/* ── LAYOUT ── */}
      <div style={{
        maxWidth:1140, margin:"0 auto",
        padding:`0 ${px}px ${isMobile?60:100}px`,
        display:"flex", gap:48, alignItems:"flex-start",
      }}>

        {/* ── ENHANCED SIDEBAR ── */}
        {!isMobile && (
          <aside style={{ width:220, flexShrink:0, position:"sticky", top:82 }}>
            <GlassCard style={{ overflow:"hidden", border:"1px solid rgba(59,130,246,0.2)" }}>
              <div style={{
                padding:"12px 18px", fontSize:10, fontWeight:700,
                letterSpacing:1.2, color:"rgba(96,165,250,0.6)",
                textTransform:"uppercase", borderBottom:"1px solid rgba(59,130,246,0.12)",
                background:"rgba(59,130,246,0.06)",
              }}>Contents</div>
              {SECTIONS.map((s,i) => {
                const active = activeId === s.id;
                const Ic = SECTION_ICONS[s.id];
                return (
                  <button key={s.id} onClick={() => scrollTo(s.id)}
                    className="priv-toc-btn"
                    style={{
                      display:"flex", width:"100%", textAlign:"left", alignItems:"center", gap:8,
                      background: active ? "rgba(59,130,246,0.12)" : "none",
                      border:"none", borderLeft:`2px solid ${active ? "#3B82F6" : "transparent"}`,
                      borderBottom:"1px solid rgba(255,255,255,0.04)",
                      cursor:"pointer", padding:"9px 14px 9px 12px",
                      fontSize:12, color: active ? "#93C5FD" : "rgba(255,255,255,0.4)",
                      fontWeight: active ? 700 : 500,
                      transition:"all 0.15s",
                    }}
                  >
                    {Ic && <Ic size={11} style={{ flexShrink:0, opacity: active?1:0.5 }} />}
                    <span style={{ fontSize:11.5 }}>{s.label}</span>
                  </button>
                );
              })}
            </GlassCard>
          </aside>
        )}

        {/* ── ARTICLE ── */}
        <article style={{ flex:1, minWidth:0, paddingTop:8 }}>

          <SectionBlock id="intro" idx={0} icon={SECTION_ICONS.intro}>
            <P><strong>Gupta Vibhor and Associates</strong> ("we", "us", "our") operates the <strong>LorryKhojo</strong> platform — a FASTag-powered fleet intelligence and vehicle tracking service accessible at <strong>lorrykhojo.com</strong>. This Privacy Policy explains how we collect, use, disclose, and protect information about you when you use our platform.</P>
            <P>This policy is compliant with the <strong>Digital Personal Data Protection Act, 2023 (DPDPA)</strong> and applicable Indian data protection regulations. By using LorryKhojo, you consent to the practices described in this policy.</P>
          </SectionBlock>

          <SectionBlock id="collect" idx={1} icon={SECTION_ICONS.collect}>
            <P>We collect the following categories of information:</P>
            <SubH>Account Information</SubH>
            <BL items={["Name and email address (provided at registration)","Phone number (optional, used for billing contact)","Password (stored as a one-way bcrypt hash — we cannot read your password)","Organisation/fleet name"]} />
            <SubH>Vehicle Data</SubH>
            <BL items={["Vehicle registration plate numbers you add to your account","Vehicle nicknames you assign","FASTag toll-crossing location data fetched from the NETC network upon your explicit request","Last known latitude/longitude and timestamp (cached for share-link functionality)"]} />
            <SubH>Payment Data</SubH>
            <BL items={["Payment amount and plan selected","Payment transaction ID and order ID (for verification and refunds)","GST invoice records","We do not store card numbers, UPI IDs, or other sensitive payment credentials — these remain with our payment gateway."]} />
            <SubH>Usage & Technical Data</SubH>
            <BL items={["API call logs: timestamp, vehicle queried, status (success/error), response time","IP address (used for rate limiting and fraud prevention)","Browser type and device type (inferred from user-agent for display optimisation)","Session tokens (JWT, stored in your browser's localStorage)"]} />
          </SectionBlock>

          <SectionBlock id="use" idx={2} icon={SECTION_ICONS.use}>
            <BL items={["To provide the core service: fetching FASTag location data for vehicles in your fleet","To authenticate your account and maintain your session","To process payments and generate GST-compliant tax invoices","To enforce your API quota and prevent abuse","To generate and serve share links so authorised parties can view a vehicle's location","To send transactional emails (payment confirmations, password reset)","To maintain audit logs for billing disputes and legal compliance","To improve platform performance and diagnose technical issues"]} />
            <InfoBox icon="🔒">We do not use your data for advertising, behavioural profiling, or resale to data brokers. Your fleet data is yours.</InfoBox>
          </SectionBlock>

          <SectionBlock id="share" idx={3} icon={SECTION_ICONS.share}>
            <P>We do not sell your personal data. We share data only in the following limited circumstances:</P>
            <BL items={["Payment Gateway — to process payments securely. The gateway's own privacy policy governs their handling of payment data.","Masters India (NETC API partner) — receives vehicle registration numbers to fetch FASTag data on your behalf.","Neon (PostgreSQL cloud) — our database provider. Data is stored on Neon's secured infrastructure in India.","Legal obligation — we may disclose data if required by a court order, law enforcement request, or regulatory authority under Indian law.","Business transfer — in the event of a merger, acquisition, or sale of assets, user data may be transferred to the acquiring entity with notice provided to affected users."]} />
          </SectionBlock>

          <SectionBlock id="cookies" idx={4} icon={SECTION_ICONS.cookies}>
            <P>LorryKhojo is a single-page web application. We use browser <strong>localStorage</strong> (not cookies) to store:</P>
            <BL items={["Your session token (JWT) — to keep you logged in across browser sessions","Your organisation profile (name, email, API quota) — for fast local rendering without repeated server round-trips"]} />
            <P>We do not use tracking cookies, advertising cookies, or any third-party analytics scripts (e.g., Google Analytics). The only external scripts loaded are our payment gateway's checkout library (loaded only when you initiate a payment) and our CDN-hosted assets.</P>
          </SectionBlock>

          <SectionBlock id="retention" idx={5} icon={SECTION_ICONS.retention}>
            <BL items={["Account data: retained for as long as your account is active. Deleted 30 days after account deletion request.","Vehicle location cache: overwritten each time you track a vehicle. Not retained beyond the most recent track event per vehicle.","FASTag call logs: retained for 12 months for billing audit purposes, then purged.","Payment records: retained for 7 years to comply with Indian GST and accounting regulations.","Share link tokens: expire when revoked by you or when the associated vehicle is deleted."]} />
          </SectionBlock>

          <SectionBlock id="security" idx={6} icon={SECTION_ICONS.security}>
            <BL items={["All data in transit is encrypted via TLS 1.2+.","Passwords are hashed using bcrypt with a work factor of 10 — we cannot recover your password.","Session tokens (JWTs) are signed with a private secret and expire after 30 days.","Database access is restricted to server-side application code — no direct public database access.","API keys and secrets are stored as environment variables, never in source code."]} />
            <InfoBox icon="⚠️">No system is completely immune to breaches. In the event of a material data breach affecting your personal data, we will notify you by email within 72 hours of becoming aware, as required by applicable law.</InfoBox>
          </SectionBlock>

          <SectionBlock id="rights" idx={7} icon={SECTION_ICONS.rights}>
            <P>Under the Digital Personal Data Protection Act, 2023, you have the right to:</P>
            <BL items={["Access — request a copy of all personal data we hold about you","Correction — request correction of inaccurate or incomplete data","Erasure — request deletion of your account and all associated data","Grievance redressal — raise a complaint with us, and if unresolved, with the Data Protection Board of India","Nominate a person to exercise these rights on your behalf"]} />
            <P>To exercise any of these rights, email <a href="mailto:namaste@lorrykhojo.com" style={{color:"#60A5FA"}} onMouseEnter={e=>e.currentTarget.style.color="#fff"} onMouseLeave={e=>e.currentTarget.style.color="#60A5FA"}>namaste@lorrykhojo.com</a> with your registered email address. We will respond within 30 days.</P>
          </SectionBlock>

          <SectionBlock id="children" idx={8} icon={SECTION_ICONS.children}>
            <P>LorryKhojo is a business-grade logistics tool intended for adults and organisations. We do not knowingly collect personal data from individuals under the age of 18. If you believe a minor has submitted data to us, please contact us and we will delete it promptly.</P>
          </SectionBlock>

          <SectionBlock id="changes" idx={9} icon={SECTION_ICONS.changes}>
            <P>We may update this Privacy Policy as we evolve the platform or as regulations change. Material changes will be communicated via email to your registered address and via an in-app notice at least <strong>14 days</strong> before they take effect. The "Last updated" date at the top of this page reflects the most recent revision.</P>
          </SectionBlock>

          <SectionBlock id="contact" idx={10} icon={SECTION_ICONS.contact}>
            <GlassCard style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:12, border:"1px solid rgba(59,130,246,0.2)" }}>
              <CRow label="Controller"><span style={{color:"rgba(255,255,255,0.82)",fontWeight:500}}>Gupta Vibhor and Associates, New Delhi, India</span></CRow>
              <CRow label="Platform"><span style={{color:"rgba(255,255,255,0.82)",fontWeight:500}}>LorryKhojo (lorrykhojo.com)</span></CRow>
              <CRow label="Email"><a href="mailto:namaste@lorrykhojo.com" style={{color:"#60A5FA",textDecoration:"none",fontWeight:600,fontSize:14}} onMouseEnter={e=>e.currentTarget.style.color="#fff"} onMouseLeave={e=>e.currentTarget.style.color="#60A5FA"}>namaste@lorrykhojo.com</a></CRow>
              <CRow label="Phone"><a href="tel:+919711775065" style={{color:"#60A5FA",textDecoration:"none",fontWeight:600,fontSize:14}} onMouseEnter={e=>e.currentTarget.style.color="#fff"} onMouseLeave={e=>e.currentTarget.style.color="#60A5FA"}>+91 97117 75065</a></CRow>
            </GlassCard>
          </SectionBlock>

          <InfoBox icon="🛡️">Last updated: <strong>July 2026</strong> · LorryKhojo is a product of <strong>GVA Labs</strong>, operated by <strong>Gupta Vibhor and Associates</strong>, New Delhi, India.</InfoBox>
        </article>
      </div>

      <DarkFooter px={px} />
    </PageWrapper>
  );
}
